import { getSession, safeRedirect } from './auth.js';
import { get, post } from './api.js';

var RESEND_REST_SECONDS = 60;

function readCopy(el) {
  try { return JSON.parse(el.dataset.i18n || '{}'); } catch (e) { return {}; }
}

function setStatus(el, message, type) {
  if (!el) return;
  el.textContent = message || '';
  if (type) el.dataset.type = type;
  else delete el.dataset.type;
}

function wireResend(button, statusEl, D, onConfirmed) {
  if (!button || button._resendReady) return;
  button._resendReady = true;
  var restTimer = null;

  function rest() {
    button.disabled = true;
    if (restTimer) clearTimeout(restTimer);
    restTimer = setTimeout(function () {
      button.disabled = false;
      button.textContent = D.resend || 'Send it again';
    }, RESEND_REST_SECONDS * 1000);
  }

  button.addEventListener('click', async function () {
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = D.resending || 'Sending...';
    setStatus(statusEl, '');

    try {
      await post('/auth/resend-verification');
      button.textContent = D.resend || 'Send it again';
      setStatus(statusEl, D.resent || 'Sent again.', 'info');
      rest();
    } catch (err) {
      if (err && err.code === 'email_already_confirmed') {
        if (onConfirmed) onConfirmed();
        return;
      }
      if (err && err.code === 'resend_too_soon') {
        button.textContent = D.resend || 'Send it again';
        setStatus(statusEl, D.wait || 'Please wait a minute, then try again.', 'attention');
        rest();
        return;
      }
      button.textContent = D.resend || 'Send it again';
      button.disabled = false;
      setStatus(statusEl, D.resendFailed || 'The email did not go through. Please try again.', 'attention');
    }
  });
}

function initCheckEmail() {
  var root = document.getElementById('check-email');
  if (!root || root._checkEmailReady) return;
  root._checkEmailReady = true;

  var locale = root.dataset.locale || 'en';
  var D = readCopy(root);

  var title = root.querySelector('[data-check-email-title]');
  var body = root.querySelector('[data-check-email-body]');
  var card = root.querySelector('[data-check-email-card]');
  var address = root.querySelector('[data-check-email-address]');
  var resend = root.querySelector('[data-check-email-resend]');
  var status = root.querySelector('[data-check-email-status]');
  var secondary = root.querySelector('[data-check-email-secondary]');
  var signedOut = root.querySelector('[data-check-email-signedout]');
  var signin = root.querySelector('[data-check-email-signin]');

  var accountHref = '/' + locale + '/me/';
  var params = new URLSearchParams(window.location.search);
  var redirect = safeRedirect(params.get('redirect'), null);
  var nextHref = redirect || accountHref;
  var nextLabel = redirect ? (D.continue || 'Continue') : (D.account || 'Go to my account');

  function showSecondary() {
    if (!secondary) return;
    secondary.href = nextHref;
    secondary.textContent = nextLabel;
    secondary.hidden = false;
  }

  function setConfirmed() {
    if (title) title.textContent = D.confirmedTitle || title.textContent;
    if (body) body.textContent = D.confirmedBody || body.textContent;
    if (card) card.hidden = true;
    if (signedOut) signedOut.hidden = true;
    setStatus(status, '');
    showSecondary();
  }

  wireResend(resend, status, D, setConfirmed);

  getSession().then(function (user) {
    if (!user) {
      if (signedOut) signedOut.hidden = false;
      if (signin) signin.href = '/' + locale + '/me/signin/?redirect=' + encodeURIComponent(nextHref);
      return;
    }

    if (user.email_confirmed) {
      setConfirmed();
      return;
    }

    if (address) {
      address.textContent = (D.sentTo || 'Sent to {email}').replace('{email}', user.email);
      address.hidden = false;
    }
    if (card) card.hidden = false;
    if (resend) resend.hidden = false;
    showSecondary();
  });
}

function initVerifyEmail() {
  var root = document.getElementById('verify-email');
  if (!root || root._verifyEmailReady) return;
  root._verifyEmailReady = true;

  var locale = root.dataset.locale || 'en';
  var D = readCopy(root);
  var token = new URLSearchParams(window.location.search).get('token');
  var accountHref = '/' + locale + '/me/';
  var signinHref = '/' + locale + '/me/signin/?redirect=' + encodeURIComponent(accountHref);

  var title = root.querySelector('[data-verify-title]');
  var body = root.querySelector('[data-verify-body]');
  var primary = root.querySelector('[data-verify-primary]');
  var resend = root.querySelector('[data-verify-resend]');
  var status = root.querySelector('[data-verify-status]');
  var signedOut = root.querySelector('[data-verify-signedout]');
  var signin = root.querySelector('[data-verify-signin]');
  var contact = root.querySelector('[data-verify-contact]');

  function showPrimary(label, href) {
    if (!primary) return;
    primary.textContent = label;
    primary.href = href;
    primary.hidden = false;
  }

  function showSignedOut() {
    if (!signedOut) return;
    signedOut.hidden = false;
    if (signin) signin.href = signinHref;
  }

  function setSuccess() {
    if (title) title.textContent = D.successTitle || title.textContent;
    if (body) body.textContent = D.successText || body.textContent;
    if (resend) resend.hidden = true;
    if (signedOut) signedOut.hidden = true;
    if (contact) contact.hidden = true;
    setStatus(status, '');
    getSession().then(function (user) {
      if (user) showPrimary(D.account || 'Go to my account', accountHref);
      else showSignedOut();
    });
  }

  function setError() {
    if (title) title.textContent = D.errorTitle || title.textContent;
    if (body) body.textContent = D.errorText || body.textContent;
    setStatus(status, '');
    getSession().then(function (user) {
      if (!user) {
        showSignedOut();
        return;
      }
      if (user.email_confirmed) {
        showPrimary(D.account || 'Go to my account', accountHref);
        return;
      }
      if (resend) {
        resend.hidden = false;
        wireResend(resend, status, D, setSuccess);
      }
    });
  }

  if (!token) {
    setError();
    return;
  }

  get('/auth/verify-email?token=' + encodeURIComponent(token)).then(setSuccess).catch(setError);
}

function initUnverifiedBanners() {
  document.querySelectorAll('[data-email-unverified]').forEach(function (banner) {
    if (banner._unverifiedReady) return;
    banner._unverifiedReady = true;

    var D = readCopy(banner);
    var body = banner.querySelector('[data-email-unverified-text]');
    var button = banner.querySelector('[data-email-unverified-resend]');
    var status = banner.querySelector('[data-email-unverified-status]');

    getSession().then(function (user) {
      if (!user || user.email_confirmed) return;
      if (body) body.textContent = (D.body || 'We sent a confirmation link to {email}.').replace('{email}', user.email);
      banner.hidden = false;
      wireResend(button, status, D, function () {
        banner.hidden = true;
        if (window.showToast) window.showToast(D.alreadyConfirmed || 'Your email is already confirmed.', 'success');
      });
    });
  });
}

document.addEventListener('page:load', function () {
  initCheckEmail();
  initVerifyEmail();
  initUnverifiedBanners();
});
